import { unwrapData, unwrapList } from '../../utils/moduleHelpers';

export const PROMO_CATEGORIES = [
  { id: 'all', label: 'All Promotions' },
  { id: 'deposit', label: 'Deposit Bonuses' },
  { id: 'cashback', label: 'Cashback Offers' },
  { id: 'rebate', label: '% Rebates' },
  { id: 'contest', label: 'Trading Contests' },
  { id: 'referral', label: 'Referral Offers' },
  { id: 'special', label: 'Special Offers' },
];

const TONES = ['#e91e63', '#28a745', '#ef6c00', '#2962ff', '#8b0000', '#7c3aed', '#0d9488'];

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toList = (payload) => {
  const data = unwrapData(payload) ?? payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.result)) return data.result;
  if (Array.isArray(data?.promotions)) return data.promotions;
  return unwrapList({ data }) || [];
};

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] != null && row[key] !== '') return row[key];
  }
  return undefined;
};

export const formatPromoDate = (value) => {
  if (!value || value === '—') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatMoney = (value) => {
  const n = toNum(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

export const getCountdown = (endsAt) => {
  const end = new Date(endsAt).getTime();
  if (!Number.isFinite(end)) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }
  const diff = Math.max(0, end - Date.now());
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    expired: diff <= 0,
  };
};

const toneForId = (id) => {
  const raw = String(id || '0');
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) hash = (hash + raw.charCodeAt(i) * (i + 1)) % TONES.length;
  return TONES[hash];
};

export const inferPromoCategory = (row = {}) => {
  const explicit = String(pick(row, 'category', 'Category') || '').toLowerCase();
  if (explicit) {
    if (explicit.includes('deposit')) return 'deposit';
    if (explicit.includes('cash')) return 'cashback';
    if (explicit.includes('rebate')) return 'rebate';
    if (explicit.includes('contest')) return 'contest';
    if (explicit.includes('refer')) return 'referral';
    if (explicit.includes('special')) return 'special';
  }

  const blob = `${pick(row, 'promotionTypeName', 'PromotionTypeName', 'triggerEvent', 'TriggerEvent', 'bonusType', 'BonusType', 'promotionName', 'PromotionName') || ''}`.toLowerCase();
  if (/deposit|firstdeposit|fund/.test(blob)) return 'deposit';
  if (/cashback|cash/.test(blob)) return 'cashback';
  if (/rebate/.test(blob)) return 'rebate';
  if (/contest|trade/.test(blob)) return 'contest';
  if (/refer|signup|sign.?up/.test(blob)) return 'referral';
  return 'special';
};

const parseHighlightPercent = (title = '', bonusPercent = 0) => {
  const fromTitle = String(title).match(/(\d+(?:\.\d+)?)\s*%/);
  if (fromTitle) return Number(fromTitle[1]);
  const pct = toNum(bonusPercent);
  return pct > 0 ? pct : null;
};

export const resolvePromoLifecycle = (row = {}) => {
  const now = Date.now();
  const start = new Date(pick(row, 'promotionStartDate', 'PromotionStartDate') || 0).getTime();
  const end = new Date(pick(row, 'promotionEndDate', 'PromotionEndDate') || 0).getTime();
  const isActiveFlag = pick(row, 'isActive', 'IsActive');
  const statusText = String(pick(row, 'status', 'Status') || '').toLowerCase();

  if (statusText.includes('expir') || statusText.includes('complet') || statusText.includes('cancel')) {
    return 'expired';
  }
  if (Number.isFinite(end) && end > 0 && end < now) return 'expired';
  if (Number.isFinite(start) && start > now) return 'upcoming';
  if (isActiveFlag === false || isActiveFlag === 0 || statusText.includes('inactive')) return 'expired';
  return 'active';
};

export const normalizeCatalogPromotion = (row = {}, index = 0) => {
  const id =
    pick(row, 'affiliatePromotionId', 'AffiliatePromotionId', 'promotionId', 'PromotionId', 'id') ??
    `promo-${index}`;
  const title = String(
    pick(row, 'promotionName', 'PromotionName', 'title', 'name') || `Promotion ${index + 1}`
  );
  const description = String(
    pick(row, 'promotionDescription', 'PromotionDescription', 'description', 'Description') || ''
  );
  const typeName = String(
    pick(row, 'promotionTypeName', 'PromotionTypeName', 'triggerEvent', 'TriggerEvent', 'bonusType', 'BonusType') ||
      'Promotion'
  );
  const startDate = pick(row, 'promotionStartDate', 'PromotionStartDate') || null;
  const endDate = pick(row, 'promotionEndDate', 'PromotionEndDate') || null;
  const minimumDeposit = toNum(pick(row, 'minimumDeposit', 'MinimumDeposit'));
  const requiredLots = toNum(pick(row, 'requiredLots', 'RequiredLots'));
  const bonusPercent = toNum(pick(row, 'bonusPercentage', 'BonusPercentage', 'bonusPercent', 'BonusPercent'));
  const bonusFixed = toNum(pick(row, 'bonusFixedAmount', 'BonusFixedAmount'));
  const lifecycle = resolvePromoLifecycle(row);
  const highlightPct = parseHighlightPercent(title, bonusPercent);

  return {
    id: String(id),
    promotionId: String(id),
    title,
    description,
    typeName,
    category: inferPromoCategory(row),
    startDate,
    endDate,
    validUntil: endDate,
    minimumDeposit,
    requiredLots,
    bonusPercent,
    bonusFixed,
    highlightPct,
    lifecycle,
    status: lifecycle,
    available: lifecycle === 'active',
    isActive: lifecycle === 'active',
    isHot: Boolean(pick(row, 'tag', 'Tag', 'theme', 'Theme')) || index === 0,
    tone: toneForId(id),
    cta: lifecycle === 'active' ? 'Claim Now' : lifecycle === 'upcoming' ? 'Coming soon' : 'Unavailable',
    raw: row,
  };
};

export const normalizeMyPromotion = (row = {}, index = 0) => {
  const id =
    pick(
      row,
      'affiliatePromotionsHistoryId',
      'AffiliatePromotionsHistoryId',
      'userPromotionId',
      'UserPromotionId',
      'id'
    ) ?? `my-promo-${index}`;
  const catalog = normalizeCatalogPromotion(row, index);
  const completedLots = toNum(pick(row, 'completedLots', 'CompletedLots'));
  const requiredLots = toNum(pick(row, 'requiredLots', 'RequiredLots')) || catalog.requiredLots;
  const bonusAmount = toNum(pick(row, 'bonusAmount', 'BonusAmount', 'bonusEarned', 'BonusEarned'));
  const bonusPercent =
    toNum(pick(row, 'bonusPercentage', 'BonusPercentage', 'bonusPercent', 'BonusPercent')) ||
    catalog.bonusPercent;
  const bonusType = String(
    pick(row, 'bonusType', 'BonusType') || catalog.raw?.bonusType || ''
  ).toLowerCase();
  const enrolledDate =
    pick(
      row,
      'enrolledDate',
      'EnrolledDate',
      'joinedDate',
      'JoinedDate',
      'claimedDate',
      'ClaimedDate',
      'createdDateTime',
      'CreatedDateTime'
    ) || null;
  const progressPct =
    requiredLots > 0
      ? Math.min(100, Math.round((completedLots / requiredLots) * 100))
      : bonusAmount > 0
        ? 100
        : 0;
  const statusRaw = String(pick(row, 'status', 'Status') || catalog.lifecycle || 'Active');
  const statusKey = statusRaw.trim().toLowerCase();
  const lifecycle =
    /reject|expir|complet|cancel|fail/i.test(statusKey) || catalog.lifecycle === 'expired'
      ? 'expired'
      : catalog.lifecycle === 'upcoming'
        ? 'upcoming'
        : /pending|active|progress|approved/i.test(statusKey)
          ? 'active'
          : catalog.lifecycle;

  const statusTone =
    statusKey === 'pending'
      ? '#d97706'
      : statusKey === 'approved'
        ? '#2563eb'
        : statusKey === 'active'
          ? '#16a34a'
          : statusKey === 'rejected' || statusKey.includes('reject')
            ? '#dc2626'
            : lifecycle === 'expired'
              ? '#6b7280'
              : catalog.tone;

  const bonusEarned =
    bonusAmount > 0
      ? formatMoney(bonusAmount)
      : bonusPercent > 0
        ? `${bonusPercent}%`
        : formatMoney(0);

  const bonusHint =
    bonusAmount <= 0 && bonusPercent > 0
      ? statusKey === 'pending'
        ? 'Pending credit'
        : 'Percentage bonus'
      : bonusType.includes('fixed')
        ? 'Fixed bonus'
        : 'Bonus earned';

  let cta = 'View offers';
  let ctaDisabled = false;
  if (statusKey === 'pending') {
    cta = 'Awaiting approval';
    ctaDisabled = true;
  } else if (statusKey === 'active' || lifecycle === 'active') {
    cta = requiredLots > 0 && completedLots < requiredLots ? 'Track progress' : 'View details';
  } else if (lifecycle === 'expired' || /reject|cancel/i.test(statusKey)) {
    cta = 'View history';
  }

  return {
    ...catalog,
    id: String(id),
    historyId: String(id),
    promotionId: String(
      pick(row, 'affiliatePromotionId', 'AffiliatePromotionId', 'promotionId', 'PromotionId') ||
        catalog.promotionId
    ),
    description: catalog.description,
    typeName: catalog.typeName || String(pick(row, 'triggerEvent', 'TriggerEvent') || 'Promotion'),
    completedLots,
    requiredLots,
    bonusAmount,
    bonusPercent,
    bonusType,
    bonusEarned,
    bonusHint,
    enrolledDate,
    joinedDate: enrolledDate,
    progressPct,
    progressLabel:
      requiredLots > 0
        ? `${completedLots.toLocaleString('en-US')} / ${requiredLots.toLocaleString('en-US')} lots`
        : bonusAmount > 0
          ? `Bonus: ${formatMoney(bonusAmount)}`
          : bonusPercent > 0
            ? `${bonusPercent}% bonus`
            : 'No lot target',
    lifecycle,
    status: statusRaw,
    statusKey,
    statusLabel: statusRaw.trim().toUpperCase() || lifecycle.toUpperCase(),
    statusTone,
    ends: catalog.endDate ? formatPromoDate(catalog.endDate) : '—',
    period:
      catalog.startDate || catalog.endDate
        ? `${formatPromoDate(catalog.startDate)} – ${formatPromoDate(catalog.endDate)}`
        : '—',
    cta,
    ctaDisabled,
    raw: row,
  };
};

export const normalizeCatalogList = (payload) =>
  toList(payload).map((row, index) => normalizeCatalogPromotion(row, index));

export const normalizeMyPromotionList = (payload) =>
  toList(payload).map((row, index) => normalizeMyPromotion(row, index));

/**
 * Promotion IDs the partner has already claimed (any non-rejected history row).
 */
export const getClaimedPromotionIds = (mine = []) => {
  const ids = new Set();
  (mine || []).forEach((p) => {
    const status = String(p.statusKey || p.status || '').toLowerCase();
    if (/reject|cancel/.test(status)) return;
    const pid = String(p.promotionId || '').trim();
    if (pid) ids.add(pid);
  });
  return ids;
};

/** Mark catalog rows already in My Promotions as claimed (Claim disabled). */
export const withClaimState = (catalog = [], mine = []) => {
  const claimedIds = getClaimedPromotionIds(mine);
  return (catalog || []).map((p) => {
    const alreadyClaimed = claimedIds.has(String(p.promotionId));
    if (!alreadyClaimed) return { ...p, alreadyClaimed: false };
    return {
      ...p,
      alreadyClaimed: true,
      available: false,
      cta: 'Already Claimed',
    };
  });
};

export const buildFeaturedPromotion = (catalog = []) => {
  if (!catalog.length) return null;
  const hot = catalog.find((p) => p.isHot && p.lifecycle === 'active' && p.available);
  const featured =
    hot ||
    catalog.find((p) => p.lifecycle === 'active' && p.available) ||
    catalog.find((p) => p.lifecycle === 'active') ||
    catalog[0];
  const pct = featured.highlightPct ?? featured.bonusPercent;
  return {
    ...featured,
    status: featured.alreadyClaimed
      ? 'CLAIMED'
      : featured.lifecycle === 'active'
        ? 'ACTIVE'
        : featured.lifecycle.toUpperCase(),
    subtitle:
      featured.description ||
      (featured.minimumDeposit > 0
        ? `Minimum deposit ${formatMoney(featured.minimumDeposit)} to claim this bonus`
        : 'Partner promotion offer'),
    bonusLabel: 'BONUS UP TO',
    bonusValue:
      pct > 0
        ? `${pct}% bonus`
        : featured.bonusFixed > 0
          ? formatMoney(featured.bonusFixed)
          : featured.title,
    eligible: featured.typeName || 'Eligible partners',
    endsAt: featured.endDate,
  };
};

export const splitMyPromotions = (mine = []) => {
  const active = mine.filter((p) => p.lifecycle === 'active' || p.lifecycle === 'upcoming');
  const history = mine.filter((p) => p.lifecycle === 'expired');
  return { active, history };
};

export const buildMyPromoStats = (mine = []) => {
  const now = Date.now();
  const week = 7 * 24 * 60 * 60 * 1000;
  const pending = mine.filter((p) => p.statusKey === 'pending').length;
  const activeNow = mine.filter(
    (p) => p.lifecycle === 'active' && p.statusKey !== 'rejected'
  ).length;
  return {
    joined: mine.length,
    active: activeNow,
    pending,
    lotsTargetMet: mine.filter((p) => p.requiredLots > 0 && p.completedLots >= p.requiredLots).length,
    endingSoon: mine.filter((p) => {
      const end = new Date(p.endDate).getTime();
      return Number.isFinite(end) && end > now && end - now <= week && p.lifecycle === 'active';
    }).length,
  };
};

export const sortCatalog = (list = [], sortBy = 'recommended') => {
  const next = [...list];
  if (sortBy === 'ending') {
    next.sort((a, b) => new Date(a.endDate || 0) - new Date(b.endDate || 0));
  } else if (sortBy === 'newest') {
    next.sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0));
  } else if (sortBy === 'name') {
    next.sort((a, b) => String(a.title).localeCompare(String(b.title)));
  } else {
    // recommended: active first, then ending soon
    next.sort((a, b) => {
      const rank = (p) => (p.lifecycle === 'active' ? 0 : p.lifecycle === 'upcoming' ? 1 : 2);
      const byLife = rank(a) - rank(b);
      if (byLife) return byLife;
      return new Date(a.endDate || 0) - new Date(b.endDate || 0);
    });
  }
  return next;
};

export const filterCatalogByCategory = (list = [], category = 'all') => {
  if (!category || category === 'all') return list;
  return list.filter((p) => p.category === category);
};

export const endsInLabel = (endDate) => {
  if (!endDate) return '—';
  const cd = getCountdown(endDate);
  if (cd.expired) return 'Ended';
  if (cd.days > 0) return `${cd.days}d ${cd.hours}h`;
  if (cd.hours > 0) return `${cd.hours}h ${cd.minutes}m`;
  return `${cd.minutes}m ${cd.seconds}s`;
};
